import { Component, OnInit, inject } from '@angular/core';
import { ComponentsModule, DialogModule, DialogService, FormFieldModule, IconModule, SnackBarService } from '@mastechnicaegroup/carelinelive-ui';
import { DIALOG_DATA, DialogRef } from '@angular/cdk/dialog';
import { Commissioner } from '@lib/api/models/commissioner';
import { FormArray, FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { NgForOf } from '@angular/common';
import { createSpecialHoursFormGroup, createWorkingHoursFormGroup } from '@lib/api/models/commissioner-working-hours';
import { CommissionerService } from '@lib/commissioner/commissioner.service';
import { forkJoin } from 'rxjs';

@Component({
    selector: 'app-edit-working-hours',
    imports: [
        DialogModule,
        FormFieldModule,
        IconModule,
        ReactiveFormsModule,
        NgForOf,
        ComponentsModule
    ],
    templateUrl: './edit-commissioner-working-hours.component.html',
    standalone: true,
    styleUrl: './edit-commissioner-working-hours.component.scss'
})
export class EditCommissionerWorkingHoursComponent implements OnInit {

    protected readonly commissioner = inject<Commissioner>(DIALOG_DATA);
    protected readonly dialogRef = inject<DialogRef>(DialogRef);
    private readonly _commissionerService = inject(CommissionerService);
    private readonly _snackBar = inject(SnackBarService);

    workingHoursFormGroup: FormGroup;
    specialHoursFormGroup: FormGroup;
    saving = false;

    fullWeekdays = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
    completeWeekdays: { id: string, weekday: string, start_time: string, end_time: string, open: boolean }[] = [];

    constructor(private fb: FormBuilder) {
        this.workingHoursFormGroup = this.fb.group({
            workingHours: this.fb.array(
                this.completeWeekdays.map((hours) => createWorkingHoursFormGroup(hours))
            )
        });

        this.specialHoursFormGroup = this.fb.group({
            special_hours: this.fb.array(
                this.commissioner?.special_hours?.map((hours) => createSpecialHoursFormGroup(hours)) || []
            )
        });
    }

    ngOnInit() {
        if (this.commissioner?.working_hours) {
            const commissionerByDay = new Map(
                this.commissioner.working_hours.map(item => [item.weekday, item])
            );

            this.completeWeekdays = this.fullWeekdays.map(day => {
                return commissionerByDay.get(day) || {
                    id: day,
                    weekday: day,
                    start_time: '',
                    end_time: '',
                    open: false
                };
            });

            this.workingHoursFormGroup = this.fb.group({
                workingHours: this.fb.array(
                    this.completeWeekdays.map((hours) => createWorkingHoursFormGroup(hours))
                )
            });
        }
    }

    get workingHoursFormArray(): FormArray<FormGroup> {
        return this.workingHoursFormGroup.get('workingHours') as FormArray<FormGroup>;
    }

    get specialHoursFormArray(): FormArray<FormGroup> {
        return this.specialHoursFormGroup.get('special_hours') as FormArray<FormGroup>;
    }

    addSpecialHour() {
        this.specialHoursFormArray.push(createSpecialHoursFormGroup());
    }

    update() {
        if (this.saving) {
            return;
        }

        this.saving = true;
        this.workingHoursFormGroup.disable();
        this.specialHoursFormGroup.disable();

        forkJoin([
            this._commissionerService.updateWorkingHours(this.commissioner.id, this.workingHoursFormGroup.value.workingHours),
            this._commissionerService.updateSpecialHours(this.commissioner.id, this.specialHoursFormGroup.value),
        ]).subscribe({
            next: () => {
                this._snackBar.open({
                    colour: 'success',
                    icon: 'check',
                    title: 'Working hours saved'
                });
                this.dialogRef.close();
            },
            error: () => {
                this.saving = false;
                this.workingHoursFormGroup.enable();
                this.specialHoursFormGroup.enable();
                this._snackBar.open({
                    colour: 'error',
                    icon: 'error',
                    title: 'Failed to save working hours'
                });
            }
        });
    }

    public static show(dialog: DialogService, data: Commissioner) {
        return dialog.openDialog<DialogRef, Commissioner>(EditCommissionerWorkingHoursComponent, {
            data
        });
    }
}
